/** Real API client — talks to the guest API and uploads directly to blob storage via SAS. */

import type {
  GuestApi,
  PropertyConfig,
  SessionFileRequest,
  SessionResponse,
  SubmitRequest
} from "../types";

async function requireOk(response: Response, what: string): Promise<Response> {
  if (!response.ok) {
    throw new Error(`${what} failed (${response.status})`);
  }
  return response;
}

export const realApi: GuestApi = {
  async fetchConfig(slug: string): Promise<PropertyConfig | null> {
    const response = await fetch(`/api/g/${encodeURIComponent(slug)}/config`);
    if (response.status === 404) return null;
    await requireOk(response, "Loading the guestbook");
    return (await response.json()) as PropertyConfig;
  },

  async createSession(slug: string, files: SessionFileRequest[]): Promise<SessionResponse> {
    const response = await fetch(`/api/g/${encodeURIComponent(slug)}/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ files })
    });
    await requireOk(response, "Preparing the upload");
    return (await response.json()) as SessionResponse;
  },

  uploadToSas(
    sasUrl: string,
    blob: Blob,
    contentType: string,
    onProgress: (fraction: number) => void
  ): Promise<void> {
    // XMLHttpRequest instead of fetch: upload progress events.
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", sasUrl);
      xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
      xhr.setRequestHeader("Content-Type", contentType);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(event.loaded / event.total);
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress(1);
          resolve();
        } else {
          reject(new Error(`Upload failed (${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error("Upload failed (network error)"));
      xhr.onabort = () => reject(new Error("Upload cancelled"));
      xhr.send(blob);
    });
  },

  async submit(slug: string, payload: SubmitRequest): Promise<void> {
    const response = await fetch(`/api/g/${encodeURIComponent(slug)}/submit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    await requireOk(response, "Finishing your submission");
  }
};
